import type { Session } from '@supabase/supabase-js'
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { safeSessionGet } from './browserStorage'
import { supabase } from './supabase'
import type { Profile } from './types'

export const DEMO_KEY = 'closedrecord:demo'
export const DEMO_USER_ID = '00000000-0000-0000-0000-000000000000'

type AuthState = {
  session: Session | null
  profile: Profile | null
  loading: boolean
  isDemo: boolean
  userId: string | null
  refreshSession: () => Promise<void>
}

const AuthContext = createContext<AuthState>({
  session: null,
  profile: null,
  loading: true,
  isDemo: false,
  userId: null,
  refreshSession: async () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const initialCheckDone = useRef(false)
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const finishInitialLoad = useCallback(async (nextSession: Session | null) => {
    setSession(nextSession)
    initialCheckDone.current = true
    if (safetyTimer.current) {
      clearTimeout(safetyTimer.current)
      safetyTimer.current = null
    }
    if (!nextSession) {
      setProfile(null)
      setLoading(false)
      return
    }
    try {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', nextSession.user.id)
        .maybeSingle()
      setProfile(data as Profile | null)
    } catch {
      setProfile(null)
    }
    setLoading(false)
  }, [])

  const refreshSession = useCallback(async () => {
    const { data } = await supabase.auth.getSession()
    if (data.session) {
      finishInitialLoad(data.session)
    }
  }, [finishInitialLoad])

  useEffect(() => {
    let cancelled = false

    const handleSession = (nextSession: Session | null) => {
      if (cancelled) return
      if (nextSession) {
        finishInitialLoad(nextSession)
      } else if (initialCheckDone.current) {
        setSession(null)
        setProfile(null)
      }
    }

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      handleSession(nextSession)
    })

    supabase.auth.getSession().then(({ data: sessionData }) => {
      if (cancelled) return
      if (sessionData.session) {
        handleSession(sessionData.session)
      } else if (!initialCheckDone.current) {
        safetyTimer.current = setTimeout(() => {
          if (!cancelled && !initialCheckDone.current) {
            finishInitialLoad(null)
          }
        }, 2000)
      }
    })

    return () => {
      cancelled = true
      data.subscription.unsubscribe()
      if (safetyTimer.current) {
        clearTimeout(safetyTimer.current)
      }
    }
  }, [finishInitialLoad])

  const isDemo = Boolean(safeSessionGet(DEMO_KEY))
  const userId = session?.user?.id ?? (isDemo ? DEMO_USER_ID : null)

  return (
    <AuthContext.Provider value={{ session, profile, loading, isDemo, userId, refreshSession }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
